import { useState, useEffect, useCallback } from 'react';
import { getData } from '../services/apiService';
import { useAuth } from '../context/AuthContext';
import { useWebSocket } from '../context/WebSocketContext';

interface LockedAccount {
  id: number;
  username: string;
  email: string;
  LockoutTime: string;
  FailedLoginAttempts: number;
}

export const useLockedAccounts = () => {
  const { user } = useAuth();
  const { lockedAccountsUpdate, clearLockedAccountsUpdate } = useWebSocket();
  const [lockedAccounts, setLockedAccounts] = useState<LockedAccount[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>('');
  const [lastUpdateTimestamp, setLastUpdateTimestamp] = useState<number>(0);

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  const fetchLockedAccounts = useCallback(async () => {
    if (!isAdmin) return;

    try {
      setLoading(true);
      setError('');
      const response = await getData('/api/users/locked-accounts');
      
      if (response.status === 'success') {
        setLockedAccounts(response.data || []);
      } else {
        setError('Failed to fetch locked accounts');
      }
    } catch (err) {
      setError('Error fetching locked accounts');
    } finally {
      setLoading(false);
    }
  }, [isAdmin]);

  useEffect(() => {
    if (isAdmin) {
      fetchLockedAccounts();
    }
  }, [isAdmin, fetchLockedAccounts]);

  // Listen for WebSocket events to update in real-time
  useEffect(() => {
    if (lockedAccountsUpdate && isAdmin && lockedAccountsUpdate.timestamp > lastUpdateTimestamp) {
      // Update timestamp to prevent duplicate processing
      setLastUpdateTimestamp(lockedAccountsUpdate.timestamp);
      
      // Refresh the accounts list when any account status changes
      fetchLockedAccounts();
      
      // Clear the update after a short delay to prevent immediate re-triggers
      setTimeout(() => {
        clearLockedAccountsUpdate();
      }, 100);
    }
  }, [lockedAccountsUpdate, isAdmin, lastUpdateTimestamp, fetchLockedAccounts, clearLockedAccountsUpdate]);

  return {
    lockedAccounts,
    lockedAccountsCount: lockedAccounts.length,
    loading,
    error,
    refetch: fetchLockedAccounts,
  };
};